import { Injectable } from '@angular/core';
import { Location, PlatformLocation } from '@angular/common';
import { Router } from '@angular/router';

import { ReplaySubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class BgxLocationService {
  private readonly urlParser: HTMLAnchorElement = document.createElement('a');
  private urlSubject: ReplaySubject<string> = new ReplaySubject<string>(1);

  currentUrl: Observable<string> = this.urlSubject.pipe(map((url: string) => this.stripSlashes(url)));

  currentPath: Observable<string> = this.currentUrl.pipe(
    map((url: string) => (url.match(/[^?#]*/) || [])[0]), // strip query and hash
  );

  constructor(private location: Location, private router: Router, private platformLocation: PlatformLocation) {
    this.urlSubject.next(location.path(true));

    this.location.subscribe((state: any) => {
      return this.urlSubject.next(state.url || '');
    });
  }

  // TODO: ignore if url-without-hash-or-search matches current location?
  go(url: string | null | undefined): void {
    if (!url) {
      return;
    }
    url = this.stripSlashes(url);
    if (/^http/.test(url)) {
      // Has http protocol so leave the site
      this.goExternal(url);
    } else {
      this.location.go(url);
      this.urlSubject.next(url);
      this.router.navigateByUrl(url);
    }
  }

  goExternal(url: string): void {
    window.location.assign(url);
  }

  replace(url: string): void {
    window.location.replace(url);
  }

  search(): any {
    const search: { [index: string]: string | undefined } = {};
    const path: string = this.location.path();
    const q: number = path.indexOf('?');
    if (q > -1) {
      try {
        const params: string[] = path.substr(q + 1).split('&');
        params.forEach((p: string) => {
          const pair: string[] = p.split('=');
          if (pair[0]) {
            search[decodeURIComponent(pair[0])] = pair[1] && decodeURIComponent(pair[1]);
          }
        });
      } catch (e) {
        /* don't care */
      }
    }
    return search;
  }

  setSearch(label: string, params: { [key: string]: string | undefined }): any {
    const search: string = Object.keys(params).reduce((acc: string, key: string) => {
      const value: string = params[key];
      return value === undefined
        ? acc
        : (acc += (acc ? '&' : '?') + `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }, '');

    this.platformLocation.replaceState({}, label, this.platformLocation.pathname + search);
  }

  /**
   * Handle user's anchor click
   *
   * @param anchor {HTMLAnchorElement} - the anchor element clicked
   * @param button Number of the mouse button held down. 0 means left or none
   * @param ctrlKey True if control key held down
   * @param metaKey True if command or window key held down
   * @return false if service navigated with `go()`; true if browser should handle it.
   *
   * Since we are using `LocationService` to navigate between docs, without the browser
   * reloading the page, we must intercept clicks on links.
   * If the link is to a document that we will render, then we navigate using `Location.go()`
   * and tell the browser not to handle the event.
   *
   * In most apps you might do this in a `LinkDirective` attached to anchors but in this app
   * we have a special situation where the `DocViewerComponent` is displaying semi-static
   * content that cannot contain directives. So all the links in that content would not be
   * able to use such a `LinkDirective`. Instead we are adding a click handler to the
   * `AppComponent`, whose element contains all the of the application and so captures all
   * link clicks both inside and outside the `DocViewerComponent`.
   */

  handleAnchorClick(
    anchor: HTMLAnchorElement,
    button: number = 0,
    ctrlKey: boolean = false,
    metaKey: boolean = false,
  ): any {
    // Check for modifier keys and non-left-button, which indicate the user wants to control navigation
    if (button !== 0 || ctrlKey || metaKey) {
      return true;
    }

    // If there is a target and it is not `_self` then we take this
    // as a signal that it doesn't want to be intercepted.
    // TODO: should we also allow an explicit `_self` target to opt-out?
    const anchorTarget: string = anchor.target;
    if (anchorTarget && anchorTarget !== '_self') {
      return true;
    }

    if (anchor.getAttribute('download') !== undefined) {
      return true; // let the download happen
    }

    const { pathname, search, hash } = anchor;
    const relativeUrl: string = pathname + search + hash;
    this.urlParser.href = relativeUrl;

    // don't navigate if external link or has extension
    if (anchor.href !== this.urlParser.href || !/\/[^/.]*$/.test(pathname)) {
      return true;
    }

    // approved for navigation
    this.go(relativeUrl);
    return false;
  }

  private stripSlashes(url: string): any {
    return url.replace(/^\/+/, '').replace(/\/+(\?|#|$)/, '$1');
  }
}
